/**
 * LegalRoom — Mission Control Phase E.
 *
 * A bridge room that surfaces:
 *   1. Legal Pod agent status chips (nda_agent, contract_agent, compliance_agent)
 *   2. Open legal tickets preview (up to 5, from decisions/unified filtered to legal)
 *   3. Deep-links into the full CLCO admin surface at /admin/legal
 *
 * Data:
 *   GET /api/admin/ops/agents                              — polled every 60s
 *   GET /api/admin/ops/decisions/unified?source=legal&
 *       include_decided=false&limit=5                      — polled every 60s
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Scale,
  FileText,
  Shield,
  ClipboardCheck,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Age helper ────────────────────────────────────────────────────────────────

function formatAge(isoString) {
  if (!isoString) return "—";
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Stakes chip color helper ──────────────────────────────────────────────────

function stakesChipClass(stakes) {
  if (stakes === "high") return "mc-chip mc-chip--critical";
  if (stakes === "medium") return "mc-chip mc-chip--amber";
  return "mc-chip mc-chip--neutral";
}

// ── AgentStatusChip ───────────────────────────────────────────────────────────

function AgentStatusChip({ agent }) {
  // Derive display name: last segment after "/" in agent_id, or agent_class
  const displayName =
    agent.agent_class ||
    (agent.agent_id
      ? agent.agent_id.split("/").pop()
      : agent.agent_id) ||
    "unknown";

  const status = agent.status_computed || agent.status || "unknown";
  const isActive = status === "active" || status === "running";

  return (
    <div
      className={`mc-chip ${isActive ? "mc-chip--green" : "mc-chip--amber"}`}
      title={`${displayName} — ${status}`}
    >
      <span
        className="mc-status-dot"
        style={{
          width: 6,
          height: 6,
          backgroundColor: isActive
            ? "var(--color-green)"
            : "#C45E28",
        }}
        aria-hidden="true"
      />
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
        {displayName}
      </span>
      <span
        style={{
          fontSize: 10,
          opacity: 0.7,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {isActive ? "ACTIVE" : "PAUSED"}
      </span>
    </div>
  );
}

// ── AgentPodSkeleton ──────────────────────────────────────────────────────────

function AgentPodSkeleton() {
  return (
    <div style={{ display: "flex", gap: 8 }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="mc-ghost-row"
          style={{ width: 130, height: 28, marginBottom: 0, borderRadius: 999 }}
        />
      ))}
    </div>
  );
}

// ── TicketSkeleton ────────────────────────────────────────────────────────────

function TicketSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="mc-ghost-row"
          style={{ animationDelay: `${i * 120}ms` }}
        />
      ))}
    </div>
  );
}

// ── LegalTicketRow ────────────────────────────────────────────────────────────

function LegalTicketRow({ decision, onGoToDecisions }) {
  const category =
    decision.category ||
    decision.source ||
    "legal";

  const age = formatAge(decision.created_at || decision.submitted_at);
  const stakes = decision.stakes || "low";

  return (
    <div className="mc-policy-row" style={{ cursor: "pointer" }} onClick={onGoToDecisions}>
      {/* category chip */}
      <span className="mc-chip mc-chip--neutral" style={{ fontSize: 10, padding: "1px 6px" }}>
        {category}
      </span>

      {/* title */}
      <span
        style={{
          flex: 1,
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          fontWeight: 500,
          color: "var(--mc-text)",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {decision.title || decision.summary || "(untitled)"}
      </span>

      {/* age */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--color-slate-500)",
          flexShrink: 0,
        }}
      >
        {age}
      </span>

      {/* stakes chip */}
      <span className={stakesChipClass(stakes)} style={{ fontSize: 10, padding: "1px 6px", flexShrink: 0 }}>
        {stakes}
      </span>

      {/* arrow */}
      <ChevronRight size={14} style={{ color: "var(--color-slate-500)", flexShrink: 0 }} aria-hidden="true" />
    </div>
  );
}

// ── LinkCard ──────────────────────────────────────────────────────────────────

function LinkCard({ icon: Icon, title, desc, onClick }) {
  return (
    <button
      type="button"
      className="mc-legal-link-card"
      onClick={onClick}
      style={{ width: "100%", textAlign: "left" }}
    >
      <span className="mc-legal-link-card-icon">
        <Icon size={18} aria-hidden="true" />
      </span>
      <span style={{ flex: 1 }}>
        <div className="mc-legal-link-card-title">{title}</div>
        <div className="mc-legal-link-card-desc">{desc}</div>
      </span>
      <ChevronRight size={14} style={{ color: "var(--color-slate-500)" }} aria-hidden="true" />
    </button>
  );
}

// ── Section divider ───────────────────────────────────────────────────────────

function SectionHeader({ label }) {
  return (
    <div className="mc-section-header" style={{ marginTop: 28, marginBottom: 12 }}>
      <span className="mc-section-title">{label}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LegalRoom() {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────

  const [legalAgents, setLegalAgents]   = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError]   = useState(null);

  const [tickets, setTickets]           = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);

  const [lastRefreshed, setLastRefreshed] = useState(null);

  const agentsTimerRef  = useRef(null);
  const ticketsTimerRef = useRef(null);

  // ── Fetch agents ───────────────────────────────────────────────────────────

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/agents`, {
        headers: authHeaders(),
      });
      const all = res.data?.agents || [];
      setLegalAgents(all.filter((a) => a.kind === "legal"));
      setAgentsError(null);
    } catch (err) {
      setAgentsError(err);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  // ── Fetch tickets ──────────────────────────────────────────────────────────

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/admin/ops/decisions/unified`,
        {
          params: { source: "legal", include_decided: false, limit: 5 },
          headers: authHeaders(),
        },
      );
      // Support both { decisions: [] } and [] shapes
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : raw?.decisions || raw?.items || [];
      setTickets(list);
      setTicketsError(null);
      setLastRefreshed(new Date());
    } catch (err) {
      setTicketsError(err);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  // ── Manual refresh ─────────────────────────────────────────────────────────

  const handleRefresh = useCallback(() => {
    fetchAgents();
    fetchTickets();
  }, [fetchAgents, fetchTickets]);

  // ── Polling setup ──────────────────────────────────────────────────────────

  useEffect(() => {
    fetchAgents();
    agentsTimerRef.current = setInterval(fetchAgents, 60_000);
    return () => clearInterval(agentsTimerRef.current);
  }, [fetchAgents]);

  useEffect(() => {
    fetchTickets();
    ticketsTimerRef.current = setInterval(fetchTickets, 60_000);
    return () => clearInterval(ticketsTimerRef.current);
  }, [fetchTickets]);

  // ── Navigation helpers ─────────────────────────────────────────────────────

  const goTo = useCallback(
    (path) => () => navigate(path),
    [navigate],
  );

  // ── Meta line ──────────────────────────────────────────────────────────────

  const metaLabel = lastRefreshed
    ? `Refreshed ${formatAge(lastRefreshed.toISOString())}`
    : "Polling every 60s";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--legal" aria-label="Legal room">
      {/* ── Room header ──────────────────────────────────────────────────────── */}
      <div className="mc-room-header">
        <div>
          <p className="mc-room-eyebrow">ROOM · LEGAL</p>
          <h1 className="mc-room-title">Legal</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="mc-room-meta">{metaLabel}</span>
          <button
            type="button"
            className="mc-pb-override"
            onClick={handleRefresh}
            aria-label="Refresh legal data"
            style={{ padding: "4px 10px" }}
          >
            <RefreshCw size={12} aria-hidden="true" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ── Legal Pod Status ──────────────────────────────────────────────────── */}
      <SectionHeader label="Legal Pod Status" />

      {agentsLoading ? (
        <AgentPodSkeleton />
      ) : agentsError && legalAgents.length === 0 ? (
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            color: "var(--color-slate-500)",
          }}
        >
          Could not load agent status.
        </p>
      ) : legalAgents.length === 0 ? (
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            color: "var(--color-slate-500)",
          }}
        >
          No legal agents registered.
        </p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {legalAgents.map((agent) => (
            <AgentStatusChip
              key={agent.agent_id || agent.id}
              agent={agent}
            />
          ))}
        </div>
      )}

      {/* ── Open Tickets Preview ──────────────────────────────────────────────── */}
      <SectionHeader label="Open Tickets Preview" />

      {ticketsLoading ? (
        <TicketSkeleton />
      ) : ticketsError && tickets.length === 0 ? (
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            color: "var(--color-slate-500)",
          }}
        >
          Could not load legal tickets.
        </p>
      ) : tickets.length === 0 ? (
        <p
          style={{
            textAlign: "center",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            color: "var(--color-slate-500)",
            padding: "24px 0",
          }}
        >
          No open legal tickets — all clear.
        </p>
      ) : (
        <>
          <div>
            {tickets.map((t) => (
              <LegalTicketRow
                key={t.id || t.decision_id}
                decision={t}
                onGoToDecisions={goTo("/admin/decisions")}
              />
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={goTo("/admin/decisions")}
              style={{
                background: "none",
                border: "none",
                color: "var(--color-green)",
                fontFamily: "var(--font-sans)",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              View all in Decisions
              <ChevronRight size={13} aria-hidden="true" />
            </button>
          </div>
        </>
      )}

      {/* ── Legal Admin Surface ───────────────────────────────────────────────── */}
      <SectionHeader label="Legal Admin Surface" />

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <LinkCard
          icon={Scale}
          title="Open CLCO Admin"
          desc="Full legal operations surface — all queues, settings, and audit trail"
          onClick={goTo("/admin/legal")}
        />
        <LinkCard
          icon={FileText}
          title="NDA Queue"
          desc="Pending non-disclosure agreements awaiting review or countersignature"
          onClick={goTo("/admin/legal?tab=tickets")}
        />
        <LinkCard
          icon={ClipboardCheck}
          title="Contract Review"
          desc="Expert and client contracts flagged for manual legal review"
          onClick={goTo("/admin/legal?tab=tickets")}
        />
        <LinkCard
          icon={Shield}
          title="Compliance Checks"
          desc="Regulatory and policy compliance items generated by the compliance agent"
          onClick={goTo("/admin/legal?tab=risks")}
        />
      </div>
    </section>
  );
}
